/**
 * Client-side Plan mode fallback when cloud worker is outdated or returns action detection.
 */

import i18n from '../../i18n';

const pf = (key, opts) => i18n.t(`modules.shared.aiAgent.planFallback.${key}`, opts);

function extractInvoiceCount(message) {
  const text = String(message || '');
  const match =
    text.match(/(\d+)\s*factures?/i) ||
    text.match(/factures?\s*[:.]?\s*(\d+)/i) ||
    text.match(/(?:creer|cr[eé]er|g[eé]n[eé]rer)\s+(\d+)/i);
  if (match?.[1]) {
    const n = parseInt(match[1], 10);
    if (n >= 1 && n <= 50) return n;
  }
  if (/\bfacture/i.test(text) && !/\d/.test(text)) return 1;
  return null;
}

function isSampleInvoiceRequest(message) {
  return /exemplaire|exemple|demo|d[eé]mo|test|al[eé]atoire|random|fictif/i.test(
    String(message || ''),
  );
}

function extractClientHint(message, clients = []) {
  const text = String(message || '');
  for (const client of clients) {
    const name = client?.name || client?.nom;
    if (name && text.toLowerCase().includes(String(name).toLowerCase())) {
      return name;
    }
  }
  const match = text.match(/(?:client|pour)\s+([A-Za-zÀ-ÿ0-9][A-Za-zÀ-ÿ0-9\s\-_.]{1,40})/i);
  return match?.[1]?.trim() || null;
}

function buildSampleInvoiceSteps(count, clients, products) {
  if (!clients.length) {
    return {
      status: 'needs_clarification',
      steps: [],
      clarifying_questions: [pf('no_clients_q1'), pf('no_clients_q2')],
    };
  }

  const steps = [
    {
      id: 'step-prep',
      title: pf('step_prep_sample_title'),
      detail: pf('step_prep_sample_detail', {
        clientCount: Math.min(count, clients.length),
        count,
      }),
      needs_confirmation: false,
    },
  ];

  for (let i = 1; i <= count; i += 1) {
    const client = clients[(i - 1) % clients.length];
    const clientName = client?.name || client?.nom || pf('client_fallback', { index: i });
    const productHint =
      products.length > 0
        ? products[(i - 1) % products.length]?.name || pf('catalog_product')
        : pf('catalog_line');

    steps.push({
      id: `step-inv-${i}`,
      title: pf('step_sample_invoice_title', { index: i, count }),
      detail: pf('step_sample_invoice_detail', { clientName, productHint }),
      needs_confirmation: true,
    });
  }

  steps.push({
    id: 'step-review',
    title: pf('step_review_title'),
    detail: pf('step_review_sample_detail', { count }),
    needs_confirmation: false,
  });

  return { status: 'ready', steps };
}

function buildInvoiceSteps(count, clientHint) {
  const steps = [
    {
      id: 'step-prep',
      title: pf('step_prep_prereq_title'),
      detail: clientHint
        ? pf('step_prep_prereq_detail_with_client', { clientHint, count })
        : pf('step_prep_prereq_detail_generic', { count }),
      needs_confirmation: false,
    },
  ];

  for (let i = 1; i <= count; i += 1) {
    steps.push({
      id: `step-inv-${i}`,
      title: pf('step_create_invoice_title', { index: i, count }),
      detail: clientHint
        ? pf('step_create_invoice_detail_with_client', { index: i, clientHint })
        : pf('step_create_invoice_detail_generic', { index: i }),
      needs_confirmation: true,
    });
  }

  steps.push({
    id: 'step-review',
    title: pf('step_review_title'),
    detail: pf('step_review_invoice_detail', { count }),
    needs_confirmation: false,
  });

  return steps;
}

function formatMarkdown(plan) {
  const lines = [
    `## ${plan.title}`,
    '',
    `**${pf('markdown_objective')}** ${plan.objective}`,
  ];

  if (plan.prerequisites?.length) {
    lines.push('', `**${pf('markdown_prerequisites')}**`);
    plan.prerequisites.forEach((p) => lines.push(`- ${p}`));
  }

  if (plan.steps?.length) {
    lines.push('', `**${pf('markdown_steps')}**`);
    plan.steps.forEach((s, i) => {
      const confirm = s.needs_confirmation ? ` *(${pf('markdown_confirmation_required')})*` : '';
      lines.push(`${i + 1}. **${s.title}** — ${s.detail}${confirm}`);
    });
  }

  if (plan.clarifying_questions?.length) {
    lines.push('', `**${pf('markdown_questions')}**`);
    plan.clarifying_questions.forEach((q) => lines.push(`- ${q}`));
  }

  return lines.join('\n');
}

export function buildClientPlanFromMessage(message, localData = {}) {
  const clients = localData?.clients || [];
  const products = localData?.products || [];
  const count = extractInvoiceCount(message);
  const sampleMode = isSampleInvoiceRequest(message);
  let clientHint = extractClientHint(message, clients);
  if (!clientHint && clients.length === 1) {
    clientHint = clients[0]?.name || clients[0]?.nom || null;
  }
  const wantsInvoice = /\bfacture/i.test(message) || count != null;

  if (!wantsInvoice) {
    return {
      status: 'needs_clarification',
      title: pf('clarify_generic_title'),
      objective: pf('clarify_generic_objective'),
      prerequisites: [],
      steps: [],
      risks: [],
      clarifying_questions: [
        pf('clarify_doc_type'),
        pf('clarify_clients'),
        pf('clarify_amounts'),
      ],
      markdown: '',
    };
  }

  const invoiceCount = count || (sampleMode ? 5 : 1);

  if (sampleMode) {
    const sample = buildSampleInvoiceSteps(invoiceCount, clients, products);
    if (sample.status === 'needs_clarification') {
      const plan = {
        status: 'needs_clarification',
        title: pf('plan_sample_title', { count: invoiceCount }),
        objective: pf('plan_sample_objective_prep', { count: invoiceCount }),
        prerequisites: [],
        steps: [],
        risks: [],
        clarifying_questions: sample.clarifying_questions,
        markdown: '',
      };
      plan.markdown = formatMarkdown(plan);
      return plan;
    }

    const plan = {
      status: 'ready',
      title: pf('plan_sample_title', { count: invoiceCount }),
      objective: pf('plan_sample_objective_create', { count: invoiceCount }),
      prerequisites: [
        pf('prereq_clients_available', { count: clients.length }),
        products.length
          ? pf('prereq_products_available', { count: products.length })
          : pf('prereq_product_catalog'),
      ],
      steps: sample.steps,
      risks: [pf('risk_confirmations_per_invoice', { count: invoiceCount }), pf('risk_sample_real_data')],
      clarifying_questions: [],
      markdown: '',
    };
    plan.markdown = formatMarkdown(plan);
    return plan;
  }

  if (!clientHint && clients.length > 1) {
    const plan = {
      status: 'needs_clarification',
      title: pf('plan_invoice_title', { count: invoiceCount }),
      objective: pf('plan_invoice_objective_prep', { count: invoiceCount }),
      prerequisites: [pf('prereq_client_identified'), pf('prereq_product_lines'), pf('prereq_amounts')],
      steps: [],
      risks: [pf('risk_bulk_without_client')],
      clarifying_questions: [
        pf('clarify_which_client', { count: invoiceCount }),
        invoiceCount > 1 ? pf('clarify_same_or_multiple_clients') : pf('clarify_products_on_invoice'),
        pf('clarify_line_amounts'),
      ],
      markdown: '',
    };
    plan.markdown = formatMarkdown(plan);
    return plan;
  }

  const steps = buildInvoiceSteps(invoiceCount, clientHint);
  const plan = {
    status: 'ready',
    title: pf('plan_invoice_title', { count: invoiceCount }),
    objective: clientHint
      ? pf('plan_invoice_objective_with_client', { count: invoiceCount, clientHint })
      : pf('plan_invoice_objective_after_validation', { count: invoiceCount }),
    prerequisites: [
      clientHint ? pf('prereq_client_named', { clientHint }) : pf('prereq_client_to_confirm'),
      pf('prereq_validated_lines'),
      pf('prereq_catalog_amounts'),
    ],
    steps,
    risks: [
      invoiceCount > 1
        ? pf('risk_confirmations_per_invoice', { count: invoiceCount })
        : pf('risk_single_confirmation'),
    ],
    clarifying_questions: [],
    markdown: '',
  };
  plan.markdown = formatMarkdown(plan);
  return plan;
}

export function formatClarificationText(plan) {
  const questions = (plan.clarifying_questions || [])
    .map((q, i) => `${i + 1}. ${q}`)
    .join('\n');
  return `${plan.objective || pf('clarify_need_details')}\n\n${questions}\n\n${pf('clarify_reply_hint')}`;
}

export function isPlanUsable(plan) {
  if (!plan?.status) return false;
  if (plan.status === 'needs_clarification') {
    return (plan.clarifying_questions?.length || 0) > 0;
  }
  if (plan.status === 'ready') {
    return (plan.steps?.length || 0) > 0;
  }
  return false;
}

export function normalizePlanModeResult(message, data, localData) {
  const cloudPlan = data?.plan;
  if (isPlanUsable(cloudPlan)) {
    return {
      response: data.response || cloudPlan.markdown,
      plan: cloudPlan,
      planStatus: cloudPlan.status,
      needsConfirmation: false,
      actionDetected: null,
      confirmationMessage: null,
    };
  }

  const fallback = buildClientPlanFromMessage(message, localData);

  if (fallback.status === 'ready' && !isPlanUsable(fallback)) {
    fallback.status = 'needs_clarification';
    fallback.clarifying_questions = [
      pf('fallback_clarify_clients'),
      pf('fallback_clarify_products'),
      pf('fallback_clarify_amounts_source'),
    ];
    fallback.steps = [];
    fallback.markdown = formatMarkdown(fallback);
  }

  const response =
    fallback.status === 'needs_clarification'
      ? formatClarificationText(fallback)
      : fallback.markdown;

  return {
    response,
    plan: fallback,
    planStatus: fallback.status,
    needsConfirmation: false,
    actionDetected: null,
    confirmationMessage: null,
  };
}
